import {
  AccountTreeOutlined,
  AddCircleOutlined,
  CalendarMonthOutlined,
  ApartmentOutlined,
  ChecklistOutlined,
  FolderOutlined,
  HandymanOutlined,
  ListAltOutlined,
  LogoutOutlined,
  TuneOutlined,
  AssessmentOutlined,
  AssignmentOutlined,
  BusinessOutlined,
  DashboardOutlined,
  DescriptionOutlined,
  GroupOutlined,
  HubOutlined,
  NotificationsOutlined,
  PeopleOutlined,
  SecurityOutlined,
  SettingsOutlined,
  WorkOutlined,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import {
  AuthModeSchema,
  CurrentSessionSchema,
  SwitchOrganizationRequestSchema,
  type CapabilityKey,
  type Permission,
} from '@nlc/contracts';

import { apiRequest, ApiError } from '../api/client.js';
import { purgeAllUploads } from '../api/uploads.js';
import { hasPermission, sessionQuery } from '../auth/session.js';
import { ErrorState, LoadingState } from '../components/states.js';
import TalkToNick from './talk-to-nick.js';
import { UploadsIndicator } from '../features/uploads/uploads-indicator.js';

const drawerWidth = 264;
type NavigationItem = {
  label: string;
  to: string;
  icon: ReactNode;
  permission: Permission;
  capability?: CapabilityKey;
};
const navigation: NavigationItem[] = [
  { label: 'Dashboard', to: '/', icon: <DashboardOutlined />, permission: 'dashboard.read' },
  { label: 'My Work', to: '/my-work', icon: <ChecklistOutlined />, permission: 'work_queue.read' },
  {
    label: 'Calendar',
    to: '/calendar',
    icon: <CalendarMonthOutlined />,
    permission: 'calendar.read',
  },
  { label: 'Intake', to: '/intake', icon: <AddCircleOutlined />, permission: 'leads.manage' },
  { label: 'Job files', to: '/job-files', icon: <FolderOutlined />, permission: 'job_files.read' },
  {
    label: 'Equipment',
    to: '/equipment',
    icon: <HandymanOutlined />,
    permission: 'equipment.read',
    capability: 'equipment',
  },
  {
    label: 'Companies',
    to: '/companies',
    icon: <BusinessOutlined />,
    permission: 'companies.read',
  },
  { label: 'Contacts', to: '/contacts', icon: <PeopleOutlined />, permission: 'contacts.read' },
  { label: 'Leads', to: '/leads', icon: <HubOutlined />, permission: 'leads.read' },
  {
    label: 'Opportunities',
    to: '/opportunities',
    icon: <WorkOutlined />,
    permission: 'opportunities.read',
  },
  {
    label: 'Projects',
    to: '/projects',
    icon: <AccountTreeOutlined />,
    permission: 'projects.read',
  },
  { label: 'Tasks', to: '/tasks', icon: <AssignmentOutlined />, permission: 'tasks.read' },
  {
    label: 'Documents',
    to: '/documents',
    icon: <DescriptionOutlined />,
    permission: 'documents.read',
  },
  { label: 'Reports', to: '/reports', icon: <AssessmentOutlined />, permission: 'reports.read' },
  {
    label: 'Notifications',
    to: '/notifications',
    icon: <NotificationsOutlined />,
    permission: 'notifications.read',
  },
  {
    label: 'Members',
    to: '/admin/members',
    icon: <GroupOutlined />,
    permission: 'memberships.read',
  },
  {
    label: 'Roles & audit',
    to: '/admin/security',
    icon: <SecurityOutlined />,
    permission: 'roles.read',
  },
  {
    label: 'Integrations',
    to: '/admin/integrations',
    icon: <ApartmentOutlined />,
    permission: 'integrations.read',
  },
  {
    label: 'Organization',
    to: '/admin/organization',
    icon: <SettingsOutlined />,
    permission: 'organizations.read',
  },
  { label: 'Catalogs', to: '/admin/catalogs', icon: <TuneOutlined />, permission: 'settings.read' },
  {
    label: 'Workflow templates',
    to: '/admin/workflow-templates',
    icon: <ListAltOutlined />,
    permission: 'workflow_templates.read',
  },
];

export default function AppShell(): React.JSX.Element {
  const session = useQuery(sessionQuery);
  const [mobileOpen, setMobileOpen] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();

  const switchOrganization = useMutation({
    mutationFn: (organizationId: string) =>
      apiRequest('/auth/organization', CurrentSessionSchema, {
        method: 'POST',
        body: JSON.stringify(SwitchOrganizationRequestSchema.parse({ organizationId })),
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(['session'], updated);
      await queryClient.invalidateQueries();
    },
  });
  const authMode = useQuery({
    queryKey: ['auth', 'mode'],
    queryFn: () => apiRequest('/auth/mode', AuthModeSchema),
    enabled: session.data?.demo === true,
    staleTime: 5 * 60 * 1000,
  });
  const logout = useMutation({
    // Local upload drafts are purged before the server ends the session, so nothing of this
    // member's stays on a shared device.
    mutationFn: async () => {
      await purgeAllUploads();
      return apiRequest('/auth/logout', CurrentSessionSchema.partial(), { method: 'POST' });
    },
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/login');
    },
  });

  if (session.isPending) return <LoadingState label="Loading your session" />;
  if (session.error instanceof ApiError && session.error.status === 401) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (session.isError)
    return (
      <Box sx={{ p: 4 }}>
        <ErrorState error={session.error} retry={() => void session.refetch()} />
      </Box>
    );
  const current = session.data;

  const drawer = (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#123047',
        color: '#fff',
      }}
    >
      <Toolbar>
        <Typography variant="h2" color="inherit">
          NLC Restoration
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,.14)' }} />
      <List sx={{ px: 1, flexGrow: 1, overflowY: 'auto' }}>
        {navigation
          .filter(
            (item) =>
              hasPermission(current, item.permission) &&
              (!item.capability || current.capabilities.includes(item.capability)),
          )
          .map((item) => (
            <ListItem key={item.to} disablePadding>
              <ListItemButton
                component={NavLink}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.5,
                  color: 'rgba(255,255,255,.82)',
                  '&.active': { bgcolor: 'rgba(255,255,255,.14)', color: '#fff' },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,.14)' }} />
      <Typography
        variant="caption"
        component="p"
        sx={{ px: 2, py: 1.5, color: 'rgba(255,255,255,.7)' }}
      >
        Online-first: uploads need a connection and are kept here until they finish.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {/* Tight on phones: at 360px the switcher, uploads badge, and sign-out must all fit. */}
          <Stack
            direction="row"
            spacing={{ xs: 0.5, sm: 2 }}
            sx={{ alignItems: 'center', minWidth: 0 }}
          >
            {current.organizations.length > 1 ? (
              <FormControl
                size="small"
                sx={{ minWidth: { xs: 120, sm: 180 }, maxWidth: { xs: 160, sm: 'none' } }}
              >
                <InputLabel id="organization-label">Organization</InputLabel>
                <Select
                  labelId="organization-label"
                  label="Organization"
                  value={current.activeOrganizationId}
                  onChange={(event) => switchOrganization.mutate(event.target.value)}
                >
                  {current.organizations.map((organization) => (
                    <MenuItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
              {current.identity.displayName}
            </Typography>
            <UploadsIndicator session={current} />
            <IconButton
              aria-label="Sign out"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              sx={{ display: { xs: 'inline-flex', sm: 'none' }, minWidth: 48, minHeight: 48 }}
            >
              <LogoutOutlined />
            </IconButton>
            <Button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Sign out
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box component="aside" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3, lg: 4 } }}
      >
        <Toolbar />
        {current.demo ? (
          <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { flex: 1, minWidth: 0 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
              }}
            >
              <span>
                Shared demo. Everything here resets on the hour, so do not enter real customer
                information.
              </span>
              {authMode.data?.pricingUrl ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  href={authMode.data.pricingUrl}
                  target="_blank"
                  rel="noopener"
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Want this for your company? Get pricing
                </Button>
              ) : null}
            </Stack>
          </Alert>
        ) : null}
        <Outlet context={{ session: current }} />
        {current.demo ? <TalkToNick /> : null}
      </Box>
    </Box>
  );
}
